import { TwoImagePanelWithForm } from '../../ui_components/popups/two_image_panels.js';
import { AcceptButton, RejectButton } from '../../ui_components/buttons.js';
import {
  addCard,
  deleteFrontImage,
  sendErrorAnalytics,
  reviseCardAnalysis,
  getCollections,
} from '../../data/update_operations.js';
import { canUseMultiCollection } from '../../utils/subscription_utils.js';

import stateManager from '../../data/state_manager.js';
import { LOG_LEVELS, logMessage } from '../../logging.js';
import { ImageModel } from '../../ui_models/two_image_container_model.js';
import { FixCornersController } from './fix_corners_controller';
import { CompsPopupController } from './popup_menu_controllers.js';
import {
  selectValueTransformer,
  center_detected_fields,
  left_detected_fields,
  right_detected_fields,
  createManualFieldsWithOptions,
  responseToFormFieldMapping,
} from './form_constants.js';

import { FeatureTour } from '../../feature_tour.js';

class FormPopupController {
  constructor(cardGrid) {
    this.cardGrid = cardGrid;
    this.leftImageModel = new ImageModel();

    this.updateFront = this.updateFront.bind(this);

    this.closeButton = new RejectButton('Discard');
    this.acceptButton = new AcceptButton('Continue');
    this.cardOptions = {};
    this.cardChoices = [];
    this.selectedCardChoiceIndex = null;
    this.headers = null;

    this.displayHelpContent = this.displayHelpContent.bind(this);
  }
  // Method to define steps with direct callbacks
  getSteps() {
    return [
      {
        elementId: 'scan_image_container',
        title: 'How does it look?',
        disableButtons: false,
        description: 'Adjust corners if the crop looks wrong.',
        position: 'below', // or 'below', etc.
        endTour: false,
        skipOnly: false,
      },
      {
        elementId: 'scan_form_container',
        title: 'Check your info',
        disableButtons: false,
        description: `Fix mistakes. Need help? See the <a href="#" class="help-link">Card Guide</a>.`,
        position: 'above', // or 'below', etc.
        linkSelector: '.help-link', // Selector for the link in the description
        callback: () => this.displayHelpContent(this.getHelpContent()),
        endTour: false,
        skipOnly: false,
      },
      {
        elementId: 'nextButton',
        title: 'Continue to the next step',
        disableButtons: true,
        description: "Click 'Continue' to keep going. We'll search for comps.",
        position: 'above', // or 'below', etc.
        endTour: false,
        skipOnly: false,
      },
      {
        elementId: 'scan_form_container',
        title: 'Fill out the rest of your card',
        disableButtons: false,
        description: 'Fill in the remaining card details.',
        position: 'above', // or 'below', etc.
        endTour: false,
        skipOnly: false,
      },
      {
        elementId: 'nextButton',
        title: 'Accept your Card',
        disableButtons: true,
        description: 'Accept to add the card to your collection.',
        position: 'above', // or 'below', etc.
        endTour: false,
        skipOnly: false,
      },
      // Add more steps as needed
    ];
  }

  // Method to display help content
  displayHelpContent(helpContent) {
    // Implement the logic to display the help content
    // For example, using the existing TwoImagePanelWithForm's method
    if (this.popup && typeof this.popup.displayHelpContent === 'function') {
      this.popup.displayHelpContent(helpContent);
    } else {
      console.warn('Popup or displayHelpContent method not found.');
    }
  }

  // Method to retrieve or define helpContent
  getHelpContent() {
    return {
      imageSrc: 'images/exampleFront.jpeg',
      description:
        'This is an example of a clear front image for card recognition.',
    };
  }

  async createAndShowPopup(cardFront, cardData, headers) {
    const { serverside_user } = stateManager.getState();
    this.headers = headers;

    stateManager.updateState({
      currentFrontCoords: cardFront.card_coordinates,
    });
    this.setCardChoices(cardData);
    this.updateCardOptions(cardData);

    const leftImage = cardFront.large_image_url;
    const useCardChoices = this.cardChoices.length > 0;
    const popup = new TwoImagePanelWithForm(
      'scan',
      useCardChoices ? [] : center_detected_fields,
      useCardChoices ? [] : left_detected_fields,
      useCardChoices ? [] : right_detected_fields,
      false,
      true
    );
    this.popup = popup;

    const helpContent = {
      imageSrc: 'images/exampleFront.jpeg',
      description:
        'This is an example of a clear front image for card recognition.',
    };

    // Add the help link to the panel
    popup.addHelpLink(helpContent);

    popup.addFixCornerButtons();

    logMessage(LOG_LEVELS.DEBUG, 'Showing add card popup', { data: cardData });

    if (useCardChoices) {
      this.renderCardChoices();
      this.acceptButton.updateText('Add Card');
    } else {
      popup.updateFormFields(
        cardData,
        responseToFormFieldMapping,
        selectValueTransformer
      );
    }

    popup.addButtonToLeft(this.closeButton, () => this.handleDiscard(headers));
    popup.addButtonToRight(this.acceptButton, () => this.handleAccept(headers));

    popup.bindCloseButton(() => this.handleClose());
    popup.bindLeftFixCorners(() =>
      this.launchFrontFixCorners(cardFront.parent_image_id, headers)
    );
    popup.disableRightFixCorners();

    this.leftImageModel.setHeaders(headers);
    const leftLocalUrl = await this.leftImageModel.fetchImage(leftImage);
    popup.setLeftImage(leftLocalUrl);

    this.featureTour = new FeatureTour(this.getSteps(), popup.panel, true);

    // Start the feature tour if the user hasn't seen it
    if (!serverside_user.has_seen_tour) {
      this.featureTour.start();
    } else {
      this.featureTour.disable();
    }

    logMessage(LOG_LEVELS.INFO, 'Add card popup shown');
  }

  destroy() {
    if (this.popup) {
      this.popup.remove();
      this.popup = null;
    }
  }

  async updateFront(card_info) {
    const new_url = card_info.card_front.large_image_url;

    const local_url = await this.leftImageModel.fetchImage(new_url);

    if (this.hasFormFieldData(card_info.card_front)) {
      this.updateForm(card_info.card_front);
    }
    await this.updateFrontImage(local_url, card_info);
    this.enableSaveButton();

    this.featureTour.show();
  }

  hasFormFieldData(card_front) {
    if (!card_front) {
      return false;
    }

    return Object.keys(responseToFormFieldMapping).some(
      (key) =>
        Object.prototype.hasOwnProperty.call(card_front, key) &&
        card_front[key] !== null &&
        card_front[key] !== undefined &&
        card_front[key] !== ''
    );
  }

  updateForm(new_card_info) {
    this.setCardChoices(new_card_info);
    if (this.cardChoices.length > 0) {
      this.renderCardChoices();
      this.acceptButton.updateText('Add Card');
      return;
    }
    this.updateCardOptions(new_card_info);
    this.popup.updateFormFields(
      new_card_info,
      responseToFormFieldMapping,
      selectValueTransformer
    );
  }

  launchFrontFixCorners(server_side_id, headers) {
    this.featureTour.hide();
    const { currentFrontImage } = stateManager.getState();

    const corner_popup = new FixCornersController(
      'front',
      currentFrontImage,
      server_side_id
    );
    corner_popup.setTour(this.featureTour);
    corner_popup.bindCompletionCallback(this.updateFront);
    corner_popup.createAndShowPopup(headers);
  }

  handleClose() {
    this.popup.resetErrors();
    stateManager.updateState({ stopProcessing: true });
  }

  handleDiscard(headers) {
    const { inPhase2, phaseOneData, currentCardFront } =
      stateManager.getState();

    if (inPhase2) {
      // Step 1: Restore Phase 1 Data
      if (!phaseOneData) {
        logMessage(
          LOG_LEVELS.ERROR,
          'Phase one data is missing. Cannot revert to Phase 1.'
        );
        return;
      }

      // Step 2: Reset the Form
      this.popup.clearForm();

      // Remove Phase 2 specific UI elements
      this.popup.addFixCornerButtons();

      // this.popup.removeSingleColumnForm(); // Assuming this method exists to remove Phase 2 form
      // this.popup.removeFixCornerButtons();  // Remove any Phase 2 specific buttons if necessary

      // Re-add Phase 1 form fields
      this.popup.addFormToPopup(
        center_detected_fields,
        left_detected_fields,
        right_detected_fields
      );
      this.popup.updateFormFields(
        phaseOneData,
        responseToFormFieldMapping,
        selectValueTransformer
      );

      // Step 3: Update UI Elements
      this.acceptButton.updateText('Continue');
      this.closeButton.updateText('Discard');

      // Re-bind any necessary events or buttons for Phase 1
      this.popup.bindLeftFixCorners(() =>
        this.launchFrontFixCorners(currentCardFront.parent_image_id, headers)
      );
      this.popup.disableRightFixCorners();

      // Step 4: Update the Application State
      stateManager.updateState({
        inPhase2: false,
        phaseOneData: null,
        tour1Completed: true,
      });

      logMessage(
        LOG_LEVELS.INFO,
        'Reverted to Phase 1 form after discard in Phase 2.'
      );
    } else {
      // Existing Discard Logic for Phase 1
      const { currentFrontRequestId, currentCardFront } =
        stateManager.getState();
      this.popup.resetErrors();
      logMessage(LOG_LEVELS.INFO, 'Popup reject button clicked.');
      deleteFrontImage(currentCardFront, headers);

      const payload = {
        front_request_id: currentFrontRequestId,
      };

      sendErrorAnalytics(headers, payload);
      stateManager.updateState({ clickActionTaken: true });
    }
  }

  async handleAccept(headers) {
    if (this.cardChoices.length > 0) {
      const selectedChoice = this.getSelectedCardChoice();
      if (!selectedChoice) {
        return;
      }
      this.processCard(
        this.cardChoiceToCardData(selectedChoice),
        headers,
        this.logError
      );
      stateManager.updateState({ userModified: false, tour1Completed: true });
      return;
    }

    const { phaseOneData, inPhase2 } = stateManager.getState();

    if (inPhase2) {
      this.handlePhaseTwo(phaseOneData, headers);
    } else {
      await this.handlePhaseOne(headers);
      this.featureTour.nextStep();
    }
  }

  handlePhaseTwo(phaseOneData, headers) {
    const phaseTwoData = this.popup.getFormData();
    const combinedData = { ...phaseOneData, ...phaseTwoData };

    this.processCard(combinedData, headers, this.logError);
    stateManager.updateState({ userModified: false, tour1Completed: true });
    this.closeButton.updateText('Discard');
    console.log(combinedData);
  }

  async handlePhaseOne(headers) {
    this.popup.resetErrors();

    if (this.popup.isValidInput() && this.handleUserModified(headers)) {
      const phaseOneData = this.popup.getFormData();
      await this.prepareForPhaseTwo(phaseOneData, headers);
    } else {
      const { serverside_user } = stateManager.getState();
      if (serverside_user.has_seen_tour) {
        this.featureTour.showCurrentStep();
      }
    }
  }

  handleUserModified(headers) {
    const { currentFrontRequestId, userModified } = stateManager.getState();

    if (userModified) {
      logMessage(
        LOG_LEVELS.INFO,
        'Sending analytics due to modified form input.'
      );
      const payload = {
        front_request_id: currentFrontRequestId,
      };
      sendErrorAnalytics(headers, payload);
    }

    logMessage(LOG_LEVELS.INFO, 'Form has valid input.');
    return true;
  }

  async updatePopup(cardFront, data, headers) {
    if (this.popup) {
      this.headers = headers;
      this.setCardChoices(data);
      this.updateCardOptions(data);
      const leftImage = cardFront.large_image_url;

      const local_url1 = await this.leftImageModel.fetchImage(leftImage);

      this.popup.setLeftImage(local_url1);

      stateManager.updateState({
        currentFrontCoords: cardFront.card_coordinates,
      });

      this.popup.clearForm();
      if (this.cardChoices.length > 0) {
        this.renderCardChoices();
        this.acceptButton.updateText('Add Card');
      } else {
        this.popup.addFormToPopup(
          center_detected_fields,
          left_detected_fields,
          right_detected_fields
        );
        this.acceptButton.updateText('Continue');
        this.popup.updateFormFields(
          data,
          responseToFormFieldMapping,
          selectValueTransformer
        );
      }
      stateManager.updateState({ inPhase2: false });

      this.popup.removeFixCornerButtons();
      this.popup.addFixCornerButtons();
      this.popup.bindLeftFixCorners(() =>
        this.launchFrontFixCorners(cardFront.parent_image_id, headers)
      );
      this.popup.disableRightFixCorners();
    }
  }

  async prepareForPhaseTwo(phaseOneData, headers) {
    stateManager.updateState({ phaseOneData: phaseOneData, inPhase2: true });

    this.popup.clearForm();
    this.popup.removeFixCornerButtons();
    this.acceptButton.updateText('Accept');
    this.closeButton.updateText('Back');

    const formFields = createManualFieldsWithOptions(
      this.cardOptions.releaseSetOptions,
      this.cardOptions.parallelOptions
    );
    const midPoint = Math.ceil(formFields.length / 2);
    const leftFields = formFields.slice(0, midPoint);
    const rightFields = formFields.slice(midPoint);

    this.popup.addFormToPopup([], leftFields, rightFields);
    this.popup.updateFormFields(
      {
        release_set: this.cardOptions.releaseSet,
        parallel: this.cardOptions.parallel,
      },
      responseToFormFieldMapping,
      selectValueTransformer
    );
    if (canUseMultiCollection()) {
      await this.populateCollectionsSelect(headers);
    }
  }

  updateCardOptions(cardData = {}) {
    const uniqueOptions = (values, selectedValue) => {
      const options = Array.isArray(values) ? values : [];
      return [...new Set([selectedValue, ...options].filter(Boolean))];
    };

    this.cardOptions = {
      releaseSet: cardData.release_set || '',
      releaseSetOptions: uniqueOptions(
        cardData.release_set_options,
        cardData.release_set
      ),
      parallel: cardData.parallel || '',
      parallelOptions: uniqueOptions(
        cardData.parallel_options,
        cardData.parallel
      ),
    };
  }

  setCardChoices(cardData = {}) {
    const choices = Array.isArray(cardData.card_choices)
      ? cardData.card_choices
      : [];
    this.cardChoices = choices
      .filter((choice) => choice && typeof choice === 'object')
      .sort(
        (left, right) =>
          Number(right.confidence || 0) - Number(left.confidence || 0)
      );
    this.selectedCardChoiceIndex = this.cardChoices.length > 0 ? 0 : null;
  }

  renderCardChoices() {
    this.popup.clearForm();
    this.popup.form.classList.add('card-choice-form');

    const heading = document.createElement('div');
    heading.className = 'card-choice-heading';
    heading.innerHTML =
      '<h2>Choose the matching card</h2><p>Select the closest identification before adding it.</p>';

    const choicesContainer = document.createElement('div');
    choicesContainer.className = 'card-choice-list';
    choicesContainer.setAttribute('role', 'radiogroup');
    choicesContainer.setAttribute(
      'aria-label',
      'Possible card identifications'
    );

    this.cardChoices.forEach((choice, index) => {
      const choiceButton = document.createElement('button');
      choiceButton.type = 'button';
      choiceButton.className = 'card-choice';
      choiceButton.dataset.choiceIndex = String(index);
      choiceButton.setAttribute('role', 'radio');
      choiceButton.setAttribute(
        'aria-checked',
        index === this.selectedCardChoiceIndex ? 'true' : 'false'
      );
      if (index === this.selectedCardChoiceIndex) {
        choiceButton.classList.add('card-choice--selected');
      }

      const confidence = Number(choice.confidence);
      const confidenceLabel = Number.isFinite(confidence)
        ? `${Math.round(confidence * 100)}% match`
        : 'Confidence unavailable';
      const title =
        choice.title ||
        [choice.year, choice.manufacturer, choice.program]
          .filter(Boolean)
          .join(' ');
      const variant = [choice.release_set, choice.parallel]
        .filter(Boolean)
        .join(' · ');
      const identifier = choice.card_identifier || choice.identifier;
      const subjects = choice.subjects || choice.players;
      const subjectText = Array.isArray(subjects)
        ? subjects.join(', ')
        : subjects;

      choiceButton.innerHTML = `
        <span class="card-choice-topline">
          <strong>${this.escapeChoiceText(subjectText || 'Unknown card subject')}</strong>
          <span class="card-choice-confidence">${confidenceLabel}</span>
        </span>
        <span class="card-choice-product">${this.escapeChoiceText(title || 'Unknown card')}</span>
        <span class="card-choice-details">${this.escapeChoiceText(
          [choice.description, identifier ? `#${identifier}` : '', variant]
            .filter(Boolean)
            .join(' · ')
        )}</span>
      `;
      choiceButton.addEventListener('click', () =>
        this.selectCardChoice(index)
      );
      choicesContainer.appendChild(choiceButton);
    });

    const feedbackContainer = document.createElement('div');
    feedbackContainer.className = 'card-feedback';

    const feedbackLabel = document.createElement('label');
    feedbackLabel.htmlFor = 'card-analysis-feedback';
    feedbackLabel.textContent = 'Something wrong with these choices?';

    const feedbackInput = document.createElement('textarea');
    feedbackInput.id = 'card-analysis-feedback';
    feedbackInput.className = 'card-feedback-input';
    feedbackInput.maxLength = 1000;
    feedbackInput.rows = 3;
    feedbackInput.placeholder =
      'Describe what appears incorrect or what details the AI should reconsider.';

    const feedbackActions = document.createElement('div');
    feedbackActions.className = 'card-feedback-actions';

    const feedbackStatus = document.createElement('span');
    feedbackStatus.className = 'card-feedback-status';
    feedbackStatus.setAttribute('aria-live', 'polite');

    const retryButton = document.createElement('button');
    retryButton.type = 'button';
    retryButton.className = 'card-feedback-button';
    retryButton.textContent = 'Try Again with Feedback';
    retryButton.addEventListener('click', () =>
      this.reviseCardChoices(feedbackInput, retryButton, feedbackStatus)
    );

    feedbackActions.append(feedbackStatus, retryButton);
    feedbackContainer.append(feedbackLabel, feedbackInput, feedbackActions);

    this.popup.form.append(heading, choicesContainer, feedbackContainer);
    this.popup.lowerContainer.append(this.popup.form);
  }

  async reviseCardChoices(feedbackInput, retryButton, feedbackStatus) {
    const feedback = feedbackInput.value.trim();
    if (!feedback) {
      feedbackStatus.textContent = 'Enter feedback before trying again.';
      feedbackInput.focus();
      return;
    }

    const { currentCardFront } = stateManager.getState();
    if (!currentCardFront?.large_image_id) {
      feedbackStatus.textContent =
        'The card image is unavailable for revision.';
      return;
    }

    retryButton.disabled = true;
    retryButton.textContent = 'Rechecking…';
    feedbackStatus.textContent = 'Reviewing the image and your feedback…';

    try {
      const revised = await reviseCardAnalysis(this.headers, {
        large_image_id: currentCardFront.large_image_id,
        feedback,
        card_choices: this.cardChoices,
      });
      this.setCardChoices(revised);
      this.renderCardChoices();
      const revisedStatus = this.popup.form.querySelector(
        '.card-feedback-status'
      );
      if (revisedStatus) {
        revisedStatus.textContent = 'Choices updated from your feedback.';
      }
    } catch (error) {
      feedbackStatus.textContent =
        error.message || 'Unable to revise the choices. Please try again.';
      retryButton.disabled = false;
      retryButton.textContent = 'Try Again with Feedback';
    }
  }

  selectCardChoice(index) {
    this.selectedCardChoiceIndex = index;
    this.popup.form.querySelectorAll('.card-choice').forEach((element) => {
      const isSelected = Number(element.dataset.choiceIndex) === index;
      element.classList.toggle('card-choice--selected', isSelected);
      element.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });
  }

  getSelectedCardChoice() {
    if (this.selectedCardChoiceIndex === null) {
      return null;
    }
    return this.cardChoices[this.selectedCardChoiceIndex] || null;
  }

  cardChoiceToCardData(choice) {
    return {
      title: choice.title || '',
      description: choice.description || '',
      card_identifier: choice.card_identifier || choice.identifier || '',
      'player_name(s)': Array.isArray(choice.subjects)
        ? choice.subjects.join('/')
        : choice.subjects || choice.players || '',
      subjects: Array.isArray(choice.subjects)
        ? choice.subjects
        : String(choice.subjects || choice.players || '')
            .split('/')
            .filter(Boolean),
      manufacturer: choice.manufacturer || '',
      program: choice.program || '',
      release_set: choice.release_set || '',
      year: choice.year || '',
      patch: choice.patch ? 'Not specified' : 'None',
      auto: Boolean(choice.autograph),
      number: choice.number || '',
      parallel: choice.parallel || '',
      grade: choice.grade || '',
      grading_company: choice.grading_company || '',
      card_type: choice.card_type || '',
      trading_card_type: choice.trading_card_type || '',
      condition: 'Not specified',
      team: choice.team_override || choice.team || '',
      position: choice.position_override || choice.position || '',
      openai_confidence: choice.confidence,
    };
  }

  escapeChoiceText(value) {
    const element = document.createElement('span');
    element.textContent = String(value);
    return element.innerHTML;
  }

  async populateCollectionsSelect(
    headers,
    selected = [],
    popupInstance = this.popup
  ) {
    if (!canUseMultiCollection()) return;
    const dropdown = popupInstance?.form.querySelector(
      '[data-field="collections"]'
    );
    if (!dropdown) return;
    const optionsWrapper = dropdown.querySelector('.checkbox-options');
    const box = dropdown.querySelector('.checkbox-dropdown-box');
    if (!optionsWrapper || !box) return;
    const errorContainer = popupInstance.form.querySelector('.form-errors');
    optionsWrapper.innerHTML = '';
    try {
      const { defaultCollection } = stateManager.getState();
      const defaultId = defaultCollection?.id;
      const collections = await getCollections(headers);
      collections.forEach((c) => {
        const label = document.createElement('label');
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.name = 'collections';
        checkbox.className = 'form-checkbox';
        checkbox.value = c.id;
        // Mark this checkbox as part of a multi-value field so that
        // form serialization treats even a single entry as an array
        checkbox.dataset.forceArray = 'true';
        if (
          selected.includes(c.id) ||
          (!selected.length && defaultId && c.id === defaultId)
        )
          checkbox.checked = true;
        const span = document.createElement('span');
        span.textContent = c.name;
        label.append(checkbox, span);
        optionsWrapper.appendChild(label);

        checkbox.addEventListener('invalid', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (errorContainer) {
            errorContainer.innerHTML = e.target.validationMessage;
            errorContainer.style.display = 'block';
          }
        });
      });

      const updateText = () => {
        const count = optionsWrapper.querySelectorAll('input:checked').length;
        box.textContent = count ? `${count} selected` : 'Select Collections';
        if (errorContainer && count > 0) {
          errorContainer.style.display = 'none';
          errorContainer.innerHTML = '';
        }
      };
      optionsWrapper.addEventListener('change', updateText);
      updateText();

      box.addEventListener('click', (e) => {
        e.stopPropagation();
        dropdown.classList.toggle('open');
      });
      document.addEventListener('click', (e) => {
        if (!dropdown.contains(e.target)) dropdown.classList.remove('open');
      });
    } catch (e) {
      console.error('Failed to load collections', e);
    }
  }

  processCard(data, headers, onError) {
    const {
      currentCardFront,
      currentFrontCoords,
      frontRotation,
      currentFrontImage,
    } = stateManager.getState();

    const { collections, team, position, ...cardData } = data;

    if (team !== undefined) {
      cardData.team_override = team;
    }
    if (position !== undefined) {
      cardData.position_override = position;
    }

    const payload = {
      card_data: cardData,
      front_large_image_id: currentCardFront.large_image_id,
      front_medium_image_id: currentCardFront.medium_image_id,
      front_parent_image_id: currentCardFront.parent_image_id,
      front_coordinates: currentFrontCoords,
      front_rotation: frontRotation,
    };

    if (canUseMultiCollection()) {
      payload.collection_ids = Array.isArray(collections)
        ? collections
        : collections
          ? [collections]
          : [];
    }

    if (currentFrontImage) {
      this.cardGrid.appendPlaceholderCard(currentFrontImage);
    }

    this.acceptButton.disable();
    this.acceptButton.updateText('Calculating Value…');

    addCard(headers, payload)
      .then(async (acceptedCard) => {
        if (acceptedCard) {
          const pricedItems = acceptedCard.priced_items || {};
          const matchedItems = acceptedCard.matched_items || {};
          const cardId = Object.keys(pricedItems).length
            ? Object.keys(pricedItems)[0]
            : Object.keys(matchedItems)[0];
          const cardPricedItems = cardId ? pricedItems[cardId] || [] : [];
          const cardMatchedItems = cardId ? matchedItems[cardId] || [] : [];

          if (acceptedCard.estimated_comps) {
            this.popup.remove();
            const compsController = new CompsPopupController();
            await compsController.showCompsPopup(
              null,
              {
                reference_card: {
                  id: acceptedCard.card_id || cardId || 'openai',
                },
                user_card: {
                  user_card_id: acceptedCard.user_card_id,
                  front_image_url_large: currentCardFront.large_image_url,
                },
              },
              cardMatchedItems,
              cardPricedItems,
              headers,
              acceptedCard.estimated_comps
            );
          }

          stateManager.updateState({ clickActionTaken: true });
          const activeId = this.getActiveCollectionId();
          await this.cardGrid.loadCards(headers, activeId);
          // Sometimes the backend takes a moment to persist the new card
          // Trigger a secondary refresh shortly after the initial load to
          // ensure the latest data is displayed.
          setTimeout(() => {
            this.cardGrid.loadCards(headers, activeId);
          }, 2000);
        } else {
          this.acceptButton.enable();
          this.acceptButton.updateText('Add Card');
          logMessage(
            LOG_LEVELS.INFO,
            'User did not accept the decision. Not proceeding to the next step.'
          );
        }
      })
      .catch((error) => {
        this.acceptButton.enable();
        this.acceptButton.updateText('Add Card');
        onError(error);
      });
  }

  getActiveCollectionId() {
    const activeTab = document.querySelector('.collection-tab.active');
    return activeTab ? Number(activeTab.dataset.collectionId) : null;
  }

  createModifiedCard(card, new_card_info) {
    if (!card) {
      return null;
    }

    // Return a new object with modified values

    if (new_card_info.card_front) {
      return {
        ...card, // Spread operator to copy properties of the original card
        large_image_id: new_card_info.card_front.large_image_id, // Assuming formData contains newLargeImageId
        medium_image_id: new_card_info.card_front.medium_image_id,
        // Include other modifications as necessary
      };
    }

    return card;
  }

  async updateFrontImage(newLocalUrl, new_card_info) {
    const { currentCardFront } = stateManager.getState();
    const newCardFront = this.createModifiedCard(
      currentCardFront,
      new_card_info
    );
    this.leftImageModel.updateLocalUrl(newLocalUrl);
    stateManager.updateState({ currentCardFront: newCardFront });
    this.popup.setLeftImage(newLocalUrl);
  }

  enableSaveButton() {
    //DUMMY
  }

  logError(error) {
    logMessage(LOG_LEVELS.ERROR, 'An error occurred', error.toString());
    stateManager.updateState({ stopProcessing: true });
  }
}

export { FormPopupController };
